import React, { useEffect, useRef, useState } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Upload, Trash2, Loader2, Copy as CopyIcon, ImageIcon } from 'lucide-react';
import { api } from '../../api/client';
import { toast } from 'sonner';

const AdminMedia = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const load = () => api.mediaList().then((r) => setItems(r || [])).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const onPick = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      for (const f of files) {
        try { await api.mediaUpload(f); }
        catch (err) { toast.error(`${f.name}: ${err?.response?.data?.detail || 'failed'}`); }
      }
      toast.success(`Uploaded ${files.length} file(s)`);
      load();
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const copy = async (url) => {
    try { await navigator.clipboard.writeText(url); toast.success('URL copied'); } catch { toast.error('Copy failed'); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this file? (Soft delete)')) return;
    try { await api.mediaDelete(id); toast.success('Deleted'); load(); }
    catch { toast.error('Delete failed'); }
  };

  return (
    <div className="space-y-6" data-testid="admin-media-page">
      <div className="flex items-center justify-between">
        <p className="text-zinc-400">Upload images once, reuse across products, branding, pages and posts. URLs returned look like <code className="text-[#d4af37]">/api/media/file/&lt;id&gt;</code>.</p>
        <div>
          <input ref={inputRef} type="file" accept="image/*" multiple className="hidden" onChange={onPick} data-testid="media-file-input" />
          <Button onClick={() => inputRef.current?.click()} disabled={uploading} className="btn-gold" data-testid="media-upload-btn">
            {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />} Upload images
          </Button>
        </div>
      </div>

      {loading && <p className="text-zinc-500">Loading…</p>}
      {!loading && items.length === 0 && (
        <Card className="bg-zinc-950 border-zinc-900 p-10 text-center">
          <ImageIcon className="h-10 w-10 mx-auto mb-3 text-zinc-700" />
          <p className="text-zinc-400">No uploads yet — click "Upload images" to add your first ones.</p>
        </Card>
      )}

      {items.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {items.map((m) => {
            const url = m.url;
            return (
              <Card key={m.id} className="bg-zinc-950 border-zinc-900 overflow-hidden flex flex-col" data-testid={`media-card-${m.id}`}>
                <div className="aspect-square bg-black flex items-center justify-center">
                  <img src={url} alt={m.original_filename} className="max-w-full max-h-full object-contain" />
                </div>
                <div className="p-3 text-xs">
                  <p className="text-zinc-200 truncate" title={m.original_filename}>{m.original_filename}</p>
                  <p className="text-zinc-500 mt-0.5">{(m.size / 1024).toFixed(0)} KB · {m.ext?.toUpperCase()}</p>
                  <div className="mt-2 flex gap-1.5">
                    <Button size="sm" variant="outline" className="btn-outline-gold h-7 px-2 text-xs flex-1" onClick={() => copy(url)} data-testid={`media-copy-${m.id}`}>
                      <CopyIcon className="h-3 w-3 mr-1" /> Copy URL
                    </Button>
                    <Button size="sm" variant="ghost" className="text-zinc-400 hover:text-rose-500 h-7 px-2" onClick={() => del(m.id)} data-testid={`media-delete-${m.id}`}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminMedia;
